import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';

interface ButtonProps {
    children: React.ReactNode;
    to?: string;
    href?: string;
    target?: string;
    rel?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    type?: 'button' | 'submit' | 'reset';
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    icon?: React.ReactNode;
    'aria-label'?: string;
    title?: string;
    'data-testid'?: string;
}

const Button: React.FC<ButtonProps> = ({
    children,
    to,
    href,
    target,
    rel,
    onClick,
    type = 'button',
    variant = 'primary',
    size = 'md',
    className = '',
    disabled = false,
    fullWidth = false,
    icon,
    'aria-label': ariaLabel,
    title,
    'data-testid': dataTestId,
}) => {
    const baseClasses =
        'inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed';

    const variantClasses = {
        primary:
            'bg-app-primary text-white shadow-lg shadow-app-primary/20 hover:bg-orange-600 hover:shadow-xl hover:shadow-app-primary/30 hover:-translate-y-0.5',
        secondary:
            'bg-white dark:bg-gray-800 text-app-text dark:text-white border border-gray-200 dark:border-gray-700 shadow-sm hover:border-app-primary hover:text-app-primary hover:-translate-y-0.5',
        outline:
            'bg-transparent border-2 border-app-primary text-app-primary hover:bg-app-primary hover:text-white hover:-translate-y-0.5',
        ghost: 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-app-primary',
    };

    const sizeClasses = {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
    };

    const widthClass = fullWidth ? 'w-full' : '';
    const combinedClasses = cn(baseClasses, variantClasses[variant], sizeClasses[size], widthClass, className);

    const content = (
        <>
            {icon && <span className="mr-2">{icon}</span>}
            {children}
        </>
    );

    if (to) {
        return (
            <Link to={to} className={combinedClasses}>
                {content}
            </Link>
        );
    }

    if (href) {
        return (
            <a href={href} target={target} rel={rel} className={combinedClasses}>
                {content}
            </a>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            className={combinedClasses}
            disabled={disabled}
            aria-label={ariaLabel}
            title={title}
            data-testid={dataTestId}
        >
            {content}
        </button>
    );
};

export default Button;
